import { WorkTaskDTO2, WorkTaskStatus } from '../api/api';
import { CONTROL_ROOM_DEPARTMENT_ID, PLANNING_DEPARTMENT_ID } from './constants';
import { isWorkPermitWaitingForTechnician } from './workPermitTaskSeed';

// Red when the user has to act on a work permit, green when one of theirs is approved and not yet seen
export type WorkPermitNotification = 'actionRequired' | 'approved';

const SEEN_APPROVED_WORK_PERMITS_KEY = 'seenApprovedWorkPermitIds';

const isApproved = (workPermit: WorkTaskDTO2) =>
  workPermit.status === WorkTaskStatus.Completed || workPermit.status === WorkTaskStatus.Processed;

export const getSeenApprovedWorkPermitIds = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_APPROVED_WORK_PERMITS_KEY) ?? '[]');
  } catch {
    return [];
  }
};

export const markApprovedWorkPermitsAsSeen = (workPermits: WorkTaskDTO2[]): string[] => {
  const seenIds = getSeenApprovedWorkPermitIds();
  const newIds = workPermits
    .filter((workPermit) => isApproved(workPermit) && workPermit.id && !seenIds.includes(workPermit.id))
    .map((workPermit) => workPermit.id as string);
  if (newIds.length === 0) return seenIds;

  const ids = [...seenIds, ...newIds];
  try {
    localStorage.setItem(SEEN_APPROVED_WORK_PERMITS_KEY, JSON.stringify(ids));
  } catch {
    // The green dot then shows again on the next visit
  }
  return ids;
};

/**
 * Kontrolrummet and Planlægning follow every work permit and act on the ones in their own department.
 * Any other user is treated as a technician, who follows the work permits they created, and has to create
 * one when the task has none yet.
 */
export const getWorkPermitNotification = (
  workPermits: WorkTaskDTO2[],
  userDepartmentId: number | undefined,
  userEmail: string,
  seenApprovedWorkPermitIds: string[]
): WorkPermitNotification | undefined => {
  const isControlRoomUser = userDepartmentId === CONTROL_ROOM_DEPARTMENT_ID;
  const isPlanningUser = userDepartmentId === PLANNING_DEPARTMENT_ID;
  const isTechnician = !isControlRoomUser && !isPlanningUser;

  if (workPermits.length === 0) return isTechnician ? 'actionRequired' : undefined;

  const followedWorkPermits = isTechnician
    ? workPermits.filter((workPermit) => workPermit.createdByEmail?.toLowerCase() === userEmail.toLowerCase())
    : workPermits;

  const isActionRequired = (workPermit: WorkTaskDTO2) => {
    const departmentId = workPermit.assignedToDepartment?.departmentId;
    if (isControlRoomUser) {
      return departmentId === CONTROL_ROOM_DEPARTMENT_ID && workPermit.status === WorkTaskStatus.Created;
    }
    if (isPlanningUser) {
      return (
        departmentId === PLANNING_DEPARTMENT_ID && !isApproved(workPermit) && workPermit.status !== WorkTaskStatus.Canceled
      );
    }
    return isWorkPermitWaitingForTechnician(workPermit);
  };

  if (followedWorkPermits.some(isActionRequired)) return 'actionRequired';
  if (
    followedWorkPermits.some(
      (workPermit) => isApproved(workPermit) && !seenApprovedWorkPermitIds.includes(workPermit.id ?? '')
    )
  ) {
    return 'approved';
  }
};
